import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/observable/throw';
import { UserPreferenceEvent } from '../../../shared/models/user-favourite-event';
import { NavigationService, ResourceService } from 'sce-core';
import { of } from 'rxjs/observable/of';

@Injectable()
export class UserPreferenceService {

  private preferenceEventSubject: Subject<UserPreferenceEvent>;
  private menuTreeCache: any[] | null = null;

  constructor(private readonly http$: HttpClient,private readonly resourceServie:ResourceService,
    public navService:NavigationService) {
    this.preferenceEventSubject = new Subject<UserPreferenceEvent>();
  }
  public autoSuggest(value: string): Observable<any[]> {
    const raw = sessionStorage.getItem('menu_routing') || sessionStorage.getItem('menuRouting');
    const parsed = raw ? JSON.parse(raw) : null;
    const routings = parsed && parsed['menuRoutings'] ? parsed['menuRoutings'] : null;

    if (!routings || typeof routings !== 'object') {
      return of([]);
    }

    const searchTerm = (value || '').toLowerCase();

    return this.getMenuTree().map((menuTree: any[]) => {
      // Direct matches on leaf items (current behavior)
      const directMatches: any[] = [];
      for (const menu of Object.keys(routings)) {
        const displayValue = this.resourceServie.get('mn_' + menu);
        if ((displayValue || '').toLowerCase().includes(searchTerm)) {
          directMatches.push({
            displayValue: displayValue,
            menuID: menu,
            route: routings[menu]
          });
        }
      }

      // Children of matching parent modules (new behavior)
      const childMatches = this.findChildrenOfMatchingParents(menuTree, searchTerm, routings);

      // Deduplicate by menuID
      const seen = new Set<string>();
      const combined = [...directMatches, ...childMatches].filter(item => {
        if (seen.has(item.menuID)) {
          return false;
        }
        seen.add(item.menuID);
        return true;
      });

      return combined.sort((a: any, b: any) => a.displayValue.localeCompare(b.displayValue));
    });
  }

  private getMenuTree(): Observable<any[]> {
    if (this.menuTreeCache) {
      return of(this.menuTreeCache);
    }
    return this.navService.getUserMenu()
      .do((menu: any[]) => this.menuTreeCache = menu);
  }

  private findChildrenOfMatchingParents(menuTree: any[], searchTerm: string, routings: any): any[] {
    const results: any[] = [];

    for (const node of menuTree) {
      const displayValue = this.resourceServie.get('mn_' + node.menuID) || node.displayValue || '';

      // If parent matches and has children, collect all navigable descendants
      if (displayValue.toLowerCase().includes(searchTerm) && node.subMenuItems && node.subMenuItems.length > 0) {
        results.push(...this.collectNavigableChildren(node, routings));
      }

      // Also search in nested children
      if (node.subMenuItems && node.subMenuItems.length > 0) {
        results.push(...this.findChildrenOfMatchingParents(node.subMenuItems, searchTerm, routings));
      }
    }

    return results;
  }

  private collectNavigableChildren(node: any, routings: any): any[] {
    const results: any[] = [];

    // If this node is navigable (exists in routings), add it
    if (node.menuID && routings[node.menuID]) {
      results.push({
        displayValue: this.resourceServie.get('mn_' + node.menuID),
        menuID: node.menuID,
        route: routings[node.menuID]
      });
    }

    // Recursively collect from children
    if (node.subMenuItems && node.subMenuItems.length > 0) {
      for (const child of node.subMenuItems) {
        results.push(...this.collectNavigableChildren(child, routings));
      }
    }

    return results;
  }
  // Refresh left menu
  public receiveEvent(): Observable<UserPreferenceEvent> {
    return this.preferenceEventSubject.asObservable();
  }

  // setting menuItem to be updated
  public notifyEvent(event: UserPreferenceEvent) {
    this.preferenceEventSubject.next(event);
  }

  public getRecentSearchItemsList() {
    const response$ = this.http$.get('api/core/recentItems')
      .catch((error: any) => {
        return Observable.throw(error.message);
      });
    return response$;
  }

  public getAllSavedSearchCriterias() {
    const response$ = this.http$.get('api/data/loadsearch/ALL')
      .catch((error: any) => {
        return Observable.throw(error.message);
      });
    return response$;
  }

  public getAllFavorites() {
    const response$ = this.http$.get('api/core/favorites')
      .catch((error: any) => {
        if (error.error.errors.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(error.error.errors['0'].validationMessage);
          }
        }
      });
    return response$;
  }

  public deleteFavorites(prefrenceId: number) {
    const response$ = this.http$.post(`api/core/delete/` + prefrenceId, {})
      .catch((error: any) => {
        if (error.error.errors.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(error.error.errors['0'].validationMessage);
          }
        }
      });
    return response$;
  }

  public fetchTablePreferences(dataTableId: string, prefType: string): Observable<Response> {
    const dataUrl$ = this.http$
      .get(`api/core/user/tablepreferences/` + dataTableId + '/' + prefType)
      .map((response: any) => {
        if (response['prefType'] === 'FAVOURITECONFIG') {
          if (!response['configuration']) {
            response['configuration'] = { 'favourite': false };
            response['dataTableId'] = dataTableId;
            response['prefType'] = 'FAVOURITECONFIG';
            response['preferenceId'] = null;
          } else { response['configuration'] = JSON.parse(response['configuration']); }
        } else if (response['prefType'] === 'COLUMNCONFIG') {
          if (!response['configuration']) {
            response['dataTableId'] = dataTableId;
            response['prefType'] = 'COLUMNCONFIG';
            response['preferenceId'] = null;
            response['configuration'] = {
              hiddenPropertiesList: [],
              pinnedPropertiesList: [],
              columnWidthsObject: {},
              propertyIndexMap: {},
              pageDefinitionObject: {
                'sortColumn': null,
                'sortOrder': 0,
                'pageSize': 10
              }  // Page Definition Default Properties
            };
          } else {
            response['configuration'] = JSON.parse(response['configuration']);
          }
        } else if (response['prefType'] === 'COLUMNGROUPCONFIG') {
          if (!response['configuration']) {
            response['configuration'] = {};
            response['dataTableId'] = dataTableId;
            response['prefType'] = 'COLUMNGROUPCONFIG';
            response['preferenceId'] = null;
          } else {
            response['configuration'] = JSON.parse(response['configuration']);
          }
        }
        return response;
      }).catch((error: any) => {
        return Observable.throw('Failed to fetch User Preferences for '
          + dataTableId + ' :: ' + error.message);
      });
    return dataUrl$;
  }

  public saveTablePreferences(userPreferences: any) {
    // [LFWM-1897] Fix - converting userPreferences.configuration to string is needed only when the type of userPreferences.configuration is an object
    if (typeof (userPreferences.configuration) === 'object') {
      userPreferences.configuration = JSON.stringify(userPreferences.configuration);
    }
    const parseToJson = function () {
      if (typeof (userPreferences.configuration) === 'string') {
        userPreferences.configuration = JSON.parse(userPreferences.configuration);
      }
    };
    const response$ = this.http$.post(`api/core/user/tablepreferences/`, userPreferences)
      .map((response: any) => {
        parseToJson();
        return response;
      }).catch((error: any) => {
        parseToJson();
        return Observable.throw(error);
      });
    return response$;
  }

}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/layout/home/services/user-preference.service.ts